import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, BACKEND } from "@/lib/api";
import { toast } from "sonner";
import {
  buildShareText, CHANNEL_URLS, openShareUrl,
  pickInstagramMode, shareInstagramNative, copyForInstagram,
  nativeShareOrCopy, copyLink, copyMessageAndLink,
  shareWithCopyThenOpen,
} from "@/lib/share";
import { ChannelIcons } from "@/components/SocialIcons";

const CHANNELS = [
  { key: "whatsapp", label: "WhatsApp" },
  { key: "telegram", label: "Telegram" },
  { key: "facebook", label: "Facebook" },
  { key: "twitter",  label: "X" },
  { key: "instagram", label: "Instagram" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "email",    label: "Email" },
];

async function svgToPng(svgUrl, w = 1200, h = 1500) {
  const res = await fetch(svgUrl, { credentials: "include" });
  const text = await res.text();
  const blob = new Blob([text], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    img.crossOrigin = "anonymous";
    await new Promise((ok, ko) => { img.onload = ok; img.onerror = ko; img.src = url; });
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d").drawImage(img, 0, 0, w, h);
    return await new Promise((r) => canvas.toBlob(r, "image/png", 0.92));
  } finally {
    URL.revokeObjectURL(url);
  }
}

export default function JourneyCardModal({ open, onOpenChange, message }) {
  const svgUrl = useMemo(
    () => {
      if (!message) return null;
      const lang = (typeof navigator !== "undefined" && navigator.language)
        ? navigator.language.split("-")[0].toLowerCase() : "en";
      return `${BACKEND}/api/messages/${message.id}/journey-card.svg?ts=${Date.now()}&lang=${encodeURIComponent(lang)}`;
    },
    [message]
  );
  const [shareId, setShareId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [instagramPanel, setInstagramPanel] = useState(false);
  const pngUrlRef = useRef(null);
  const [pngUrl, setPngUrl] = useState(null);

  const setPng = (url) => {
    if (pngUrlRef.current) URL.revokeObjectURL(pngUrlRef.current);
    pngUrlRef.current = url;
    setPngUrl(url);
  };

  useEffect(() => {
    if (!open || !message) return;
    setPng(null);
    setInstagramPanel(false);
    (async () => {
      try {
        const r = await api.post(`/messages/${message.id}/share`, { channel: "journey_card" });
        setShareId(r.data.share_id);
      } catch (_) {}
      try {
        const blob = await svgToPng(svgUrl);
        if (blob) setPng(URL.createObjectURL(blob));
      } catch (_) {}
    })();
    // eslint-disable-next-line
  }, [open, message]);

  useEffect(() => () => { if (pngUrlRef.current) URL.revokeObjectURL(pngUrlRef.current); }, []);

  if (!message) return null;
  const link = shareId
    ? `${window.location.origin}/message/${message.id}?ref=${shareId}`
    : `${window.location.origin}/message/${message.id}`;
  const shareText = buildShareText(message.title);
  const compactText = buildShareText(message.title, { compact: true });

  const logShare = (channelKey) => {
    if (!shareId) return;
    api.post(`/messages/${message.id}/share`,
      { channel: channelKey, parent_ref: shareId }).catch(() => {});
  };

  // Sync window.open — preserves user gesture on mobile Safari.
  const openChannel = (ch) => {
    if (ch.key === "instagram") {
      const { mode } = pickInstagramMode();
      if (mode === "native") {
        shareInstagramNative({ url: link, text: shareText, title: message.title });
        logShare(ch.key);
      } else {
        setInstagramPanel(true);
      }
      return;
    }
    const textForChannel = ch.key === "twitter" ? compactText : shareText;
    if (ch.key === "facebook" || ch.key === "linkedin") {
      const opened = shareWithCopyThenOpen({
        platform: ch.key, url: link, text: shareText,
      });
      if (!opened) toast.error("Your browser blocked the share window.");
      logShare(ch.key);
      return;
    }
    const urlBuilder = CHANNEL_URLS[ch.key];
    if (!urlBuilder) return;
    const opened = openShareUrl(urlBuilder(link, textForChannel));
    if (!opened) toast.error("Your browser blocked the share window.");
    logShare(ch.key);
  };

  const doCopy = async () => {
    const ok = await copyLink(link);
    if (ok) logShare("copy");
  };

  const doCopyMessageAndLink = async () => {
    const ok = await copyMessageAndLink({ url: link, text: shareText });
    if (ok) logShare("copy_message");
  };

  const downloadPng = async () => {
    setBusy(true);
    try {
      let blob = null;
      if (pngUrl) {
        const r = await fetch(pngUrl); blob = await r.blob();
      } else {
        blob = await svgToPng(svgUrl);
      }
      if (!blob) throw new Error("no image");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `voihu-${message.id}.png`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      toast.success("Image saved.");
      logShare("save_image");
    } catch (e) { toast.error("Could not save image"); }
    finally { setBusy(false); }
  };

  const doNativeShare = async () => {
    setBusy(true);
    try {
      let files;
      try {
        const blob = pngUrl ? await (await fetch(pngUrl)).blob() : await svgToPng(svgUrl);
        if (blob) files = [new File([blob], "voihu.png", { type: "image/png" })];
      } catch (_) {}
      const res = await nativeShareOrCopy({
        url: link, text: shareText, title: message.title, files,
      });
      if (res.via === "native" || res.via === "clipboard") logShare("share");
    } finally { setBusy(false); }
  };

  const doInstagramCopy = async () => {
    const ok = await copyForInstagram({ url: link, text: shareText });
    if (ok) logShare("instagram");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[color:var(--oh-bg)] border oh-hairline rounded-none max-w-2xl p-0 w-[calc(100vw-2rem)] max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain"
        data-testid="journey-card-modal"
      >
        {instagramPanel ? (
          <div className="px-6 sm:px-10 py-8 pb-[max(2rem,env(safe-area-inset-bottom))]"
               data-testid="jc-instagram-panel">
            <DialogTitle className="oh-label">Ready for Instagram</DialogTitle>
            <h2 className="font-serif-editorial text-3xl sm:text-4xl leading-[1.05] mt-3">
              Your Journey Card is ready.
            </h2>
            <p className="text-[color:var(--oh-ink-2)] mt-4 text-sm leading-relaxed">
              Post it, add it to your Story, or send it in a DM.
            </p>

            {pngUrl && (
              <div className="mt-6 border oh-hairline overflow-hidden max-w-sm mx-auto">
                <img src={pngUrl} alt="Voice Journey Card" className="w-full h-auto block" />
              </div>
            )}

            <div className="mt-6 border oh-hairline p-5 text-sm leading-relaxed text-[color:var(--oh-ink)] whitespace-pre-line"
                 data-testid="jc-instagram-preview">
              {shareText}
              {"\n\n"}{link}
            </div>

            <div className="mt-6 grid grid-cols-2 gap-2">
              <button onClick={downloadPng} disabled={busy}
                      className="btn-ghost flex items-center justify-center gap-2"
                      data-testid="jc-instagram-save">
                {ChannelIcons.download && <ChannelIcons.download className="w-4 h-4" />}
                <span>{busy ? "…" : "Save image"}</span>
              </button>
              <button onClick={doInstagramCopy}
                      className="btn-primary flex items-center justify-center gap-2"
                      data-testid="jc-instagram-copy">
                {ChannelIcons.copy && <ChannelIcons.copy className="w-4 h-4" />}
                <span>Copy text + link</span>
              </button>
            </div>
            <button onClick={() => setInstagramPanel(false)}
                    className="btn-ghost w-full mt-2"
                    data-testid="jc-instagram-back">
              Back
            </button>
          </div>
        ) : (
        <div className="px-6 sm:px-10 py-6 sm:py-8 pb-[max(2rem,env(safe-area-inset-bottom))]">
          <DialogTitle className="oh-label">Voice journey card</DialogTitle>
          <h2 className="font-serif-editorial text-2xl sm:text-4xl leading-[1.05] mt-3">
            Share where your voice travelled.
          </h2>

          <div className="mt-6 border oh-hairline overflow-hidden">
            {svgUrl && (
              <img src={svgUrl} alt="Voice Journey Card"
                   className="w-full h-auto block"
                   data-testid="journey-card-image" />
            )}
          </div>

          <div className="mt-6 grid grid-cols-4 sm:grid-cols-7 gap-2">
            {CHANNELS.map((ch) => {
              const Icon = ChannelIcons[ch.key];
              return (
                <button key={ch.key} onClick={() => openChannel(ch)}
                        className="flex flex-col items-center justify-center gap-2 py-3 border oh-hairline hover:bg-[color:var(--oh-bg-2)] transition-colors"
                        data-testid={`jc-share-${ch.key}`}>
                  {Icon && <Icon className="w-5 h-5 text-[color:var(--oh-ink)]" />}
                  <span className="text-[10px] uppercase tracking-wider text-[color:var(--oh-ink)]">
                    {ch.label}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
            <button onClick={doCopy}
                    className="btn-ghost flex items-center justify-center gap-2"
                    data-testid="jc-copy">
              {ChannelIcons.copy && <ChannelIcons.copy className="w-4 h-4" />}
              <span>Copy link</span>
            </button>
            <button onClick={doCopyMessageAndLink}
                    className="btn-ghost flex items-center justify-center gap-2"
                    data-testid="jc-copy-message">
              {ChannelIcons.copy && <ChannelIcons.copy className="w-4 h-4" />}
              <span>Copy message + link</span>
            </button>
            <button onClick={downloadPng} disabled={busy}
                    className="btn-ghost flex items-center justify-center gap-2"
                    data-testid="jc-download">
              {ChannelIcons.download && <ChannelIcons.download className="w-4 h-4" />}
              <span>{busy ? "…" : "Save image"}</span>
            </button>
            <button onClick={doNativeShare} disabled={busy}
                    className="btn-primary flex items-center justify-center gap-2"
                    data-testid="jc-native">
              {ChannelIcons.share && <ChannelIcons.share className="w-4 h-4" />}
              <span>Share</span>
            </button>
          </div>

          <p className="mt-5 text-xs text-[color:var(--oh-ink-3)] leading-relaxed">
            The card represents the journey of your VOICE, not personal status. Your name is not shown.
            Anyone opening this link arrives at the MESSAGE and their voice joins your branch.
          </p>
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
